import { AllInclusivePlan } from '../constants';
import type { AllInclusivePlanLevels, Inclusiveness } from '../interfaces';

import { isPromotionalOffer, publicOrLoyaltyOffers } from './isOffer.helper';

import type { Hotel, RoomOffers } from '~~/domains/graphql';

/**
 * __hotel__
 *  - level : Based on the hotel
 *  - optional : Based on the offer being all inclusive
 *
 * __offers__
 *  - level : Based on the hotel
 *  - optional : Based on the offer being all inclusive, or if any other offer is all inclusive
 *
 * __offer-only__
 *  - level : Based on the hotel and the offer being all inclusive
 *  - optional : Always false
 *
 * @see https://jira.kaliop.net/browse/ACCORDAI-1297
 */
type InclusivenessType = 'hotel' | 'offers' | 'offer-only';

export function isOfferAllInclusive(offer?: RoomOffers): boolean {
  return (offer?.mealPlan?.toLowerCase?.() ?? '') === 'all';
}

export function getAllInclusivePlan({
  type,
  hotel,
  offers,
  isLogged,
  forcedMealPlan,
}: {
  type: InclusivenessType;
  hotel?: Hotel;
  offers?: RoomOffers | Array<RoomOffers>;
  isLogged?: boolean;
  forcedMealPlan?: string;
}): Inclusiveness {
  const hotelInclusiveness = hotel?.hotelInclusiveness as
    | AllInclusivePlanLevels
    | undefined;
  const offer = getCheapestOffer(offers, isLogged);
  const offerIsAllInclusive =
    forcedMealPlan && forcedMealPlan.toLowerCase() === 'all'
      ? true
      : isOfferAllInclusive(offer);
  const hasAnotherOfferAllInclusive = haveAnotherAllInclusiveOffer(
    offers,
    offer,
  );

  const optional = getOptionality({
    hasAnotherOfferAllInclusive,
    offerIsAllInclusive,
    type,
  });

  const level =
    offer || forcedMealPlan
      ? getLevel({
          hasAnotherOfferAllInclusive,
          offerIsAllInclusive,
          type,
          hotelInclusiveness,
        })
      : AllInclusivePlan.None;

  return { optional, level };
}

function getCheapestOffer(
  offers?: RoomOffers | Array<RoomOffers>,
  isLogged?: boolean,
): RoomOffers | undefined {
  if (!Array.isArray(offers)) return offers;

  const publicOffers = publicOrLoyaltyOffers(offers);

  publicOffers?.sort(
    (o1, o2) =>
      (o1?.pricing?.amount?.hotelKeeper ?? 0) -
      (o2?.pricing?.amount?.hotelKeeper ?? 0),
  );

  const publicOffer = publicOffers?.[0];

  let memberOffer: RoomOffers | undefined;

  if (publicOffer) {
    const memberOffers = offers.filter(offer =>
      offer?.categories?.some(isPromotionalOffer),
    );

    memberOffer = memberOffers?.find(offer =>
      offer?.code?.startsWith(publicOffer.code ? `${publicOffer.code}-` : ''),
    ) as RoomOffers | undefined;
  }

  return isLogged && memberOffer ? memberOffer : (publicOffer as RoomOffers);
}

function haveAnotherAllInclusiveOffer(
  offers?: RoomOffers | Array<RoomOffers>,
  cheapestOffer?: RoomOffers,
) {
  if (!Array.isArray(offers)) return false;

  const remainingOffers = offers.filter(
    offer => offer.code !== cheapestOffer?.code,
  );

  return remainingOffers.some(
    offer => (offer?.mealPlan?.toLowerCase?.() ?? '') === 'all',
  );
}

function getLevel({
  type,
  hotelInclusiveness,
  offerIsAllInclusive,
  hasAnotherOfferAllInclusive,
}: {
  type: InclusivenessType;
  hotelInclusiveness?: AllInclusivePlanLevels;
  offerIsAllInclusive: boolean;
  hasAnotherOfferAllInclusive: boolean;
}): AllInclusivePlanLevels {
  switch (type) {
    case 'hotel':
      return hotelInclusiveness ?? AllInclusivePlan.None;

    case 'offers': {
      if (offerIsAllInclusive || hasAnotherOfferAllInclusive) {
        return hotelInclusiveness ?? AllInclusivePlan.None;
      } else {
        return AllInclusivePlan.None;
      }
    }

    case 'offer-only':
      return offerIsAllInclusive
        ? hotelInclusiveness ?? AllInclusivePlan.None
        : AllInclusivePlan.None;

    default:
      return AllInclusivePlan.None;
  }
}

function getOptionality({
  type,
  offerIsAllInclusive,
  hasAnotherOfferAllInclusive,
}: {
  type: InclusivenessType;
  offerIsAllInclusive: boolean;
  hasAnotherOfferAllInclusive: boolean;
}): boolean {
  switch (type) {
    case 'hotel':
      return !offerIsAllInclusive;

    case 'offers':
      return !offerIsAllInclusive && hasAnotherOfferAllInclusive;

    case 'offer-only':
    default:
      return false;
  }
}
